// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import type { ChatMessage } from '../../types';

interface ChatMessageListProps {
  messages: ChatMessage[];
  isThinking: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

function ChatMessageList({ messages, isThinking, messagesEndRef }: ChatMessageListProps) {
  const navigate = useNavigate();

  return (
    <div className="chat-messages">
      {messages.map((message, index) => (
        <div
          key={index}
          className={`chat-message ${message.role === 'user' ? 'chat-message-user' : 'chat-message-assistant'}`}
        >
          <div className="message-avatar">{message.role === 'user' ? '👤' : '🤖'}</div>
          <div className="message-content">
            <div className="message-text">
              {!message.content && isThinking && index === messages.length - 1 ? (
                <span className="thinking-indicator">
                  <span className="thinking-dot"></span>
                  <span className="thinking-dot"></span>
                  <span className="thinking-dot"></span>
                </span>
              ) : message.role === 'assistant' ? (
                <ReactMarkdown
                  components={{
                    a: ({ href, children, ...props }) => {
                      const isInternal =
                        href && (href.startsWith('/') || href.startsWith(window.location.origin));
                      if (isInternal) {
                        const path = href.startsWith('/')
                          ? href
                          : href.replace(window.location.origin, '');
                        return (
                          <a
                            {...props}
                            href={path}
                            onClick={e => {
                              e.preventDefault();
                              navigate(path);
                            }}
                          >
                            {children}
                          </a>
                        );
                      }
                      return (
                        <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              ) : (
                message.content
              )}
            </div>
            {message.content && (
              <div className="message-timestamp">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default ChatMessageList;
